import {
  Button,
  Card,
  CardContent,
  EmptyState,
  ErrorState,
  LoadingSkeleton,
  PageHeader,
  cn,
  formatDateTime,
} from '@berky/ui'
import { MessageSquareText } from 'lucide-react'
import { Link, useParams, useSearchParams, useLocation } from 'react-router'

import { FeedbackStatusBadge } from './management/components/feedback-status'
import { FeedbackDetailPage } from './management/pages/feedback-detail'
import { useMyFeedback, type MyFeedbackSummary } from './my-feedback-api'
import { UnreadBadge } from './unread-badge'

/** Persönliche Sicht auf eigenes Feedback und die Antworten des Plattformteams. */
export function MyFeedbackPage() {
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedPage = Number(searchParams.get('page') ?? 1)
  const page = Number.isInteger(requestedPage)
    ? Math.max(1, Math.min(100_000, requestedPage))
    : 1
  const list = useMyFeedback(50, page)

  return (
    <>
      <PageHeader
        title="Mein Feedback"
        description="Ihre Rückmeldungen und die Antworten des Plattformteams."
      />

      {list.isPending ? (
        <LoadingSkeleton variant="text" rows={6} />
      ) : list.isError ? (
        <ErrorState
          title="Feedback konnte nicht geladen werden"
          description="Bitte versuchen Sie es später erneut."
          onRetry={() => void list.refetch()}
        />
      ) : list.data.items.length === 0 && !id ? (
        <EmptyState
          title={page > 1 ? 'Keine Rückmeldungen auf dieser Seite' : 'Noch kein Feedback'}
          description={
            page > 1
              ? 'Blättern Sie zurück, um Ihre Rückmeldungen zu sehen.'
              : 'Über den Feedbackknopf können Sie jederzeit eine Idee oder Rückmeldung senden.'
          }
        />
      ) : (
        <div className="grid gap-6 xl:grid-cols-5">
          <div className={cn('min-w-0 xl:col-span-2', id && 'hidden xl:block')}>
            <Card className="gap-0 py-0 shadow-xs">
              <CardContent className="divide-y px-0 py-0">
                {list.data.items.map((entry) => (
                  <FeedbackListEntry
                    key={entry.id}
                    entry={entry}
                    selected={id === entry.id}
                  />
                ))}
              </CardContent>
            </Card>
          </div>

          <div
            className={cn(
              'min-w-0 xl:col-span-3 xl:pl-6',
              !id && 'hidden xl:flex xl:items-center xl:justify-center',
            )}
          >
            {id ? (
              <FeedbackDetailPage management={false} />
            ) : (
              <EmptyState
                title="Rückmeldung auswählen"
                description="Wählen Sie links einen Eintrag, um Details und Gespräch zu öffnen."
              />
            )}
          </div>
        </div>
      )}
      {(page > 1 || (list.data?.total ?? 0) > 50) && (
        <nav
          aria-label="Feedbackseiten"
          className={cn('mt-4 flex flex-wrap items-center gap-3', id && 'hidden xl:flex')}
        >
          <Button
            variant="outline"
            disabled={page <= 1 || list.isPending}
            onClick={() => setSearchParams({ page: String(page - 1) })}
          >
            Zurück
          </Button>
          <span className="text-sm text-muted-foreground">
            Seite {page} · {list.data?.total ?? 0} Rückmeldungen
          </span>
          <Button
            variant="outline"
            disabled={page * 50 >= (list.data?.total ?? 0) || list.isPending}
            onClick={() => setSearchParams({ page: String(page + 1) })}
          >
            Weiter
          </Button>
        </nav>
      )}
    </>
  )
}

function FeedbackListEntry({
  entry,
  selected,
}: {
  entry: MyFeedbackSummary
  selected: boolean
}) {
  const location = useLocation()
  return (
    <Link
      to={`/mein-feedback/${entry.id}${location.search}`}
      aria-current={selected ? 'page' : undefined}
      className={cn(
        'block px-4 py-4 transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        selected && 'bg-accent',
      )}
    >
      <div className="flex items-start gap-3">
        <MessageSquareText
          className="mt-0.5 size-4 shrink-0 text-primary"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-xs text-muted-foreground">{entry.reference}</p>
          <div className="flex items-start gap-2">
            <p className="line-clamp-2 min-w-0 flex-1 text-sm font-medium">
              {entry.text}
            </p>
            <UnreadBadge count={entry.unreadMessages} />
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <time className="text-xs text-muted-foreground" dateTime={entry.angelegtAm}>
              {formatDateTime(entry.angelegtAm)}
            </time>
            <FeedbackStatusBadge status={entry.status} />
          </div>
        </div>
      </div>
    </Link>
  )
}
